import { z } from 'zod';

import { GoalCategory, GoalStatus, GoalTaskStatus } from '../goal/Goals';
import { GuidanceSessionType } from '../guidance-session/GuidanceSession';

export const POST_SESSION_WORKFLOW_ID = 'post-session-workflow';
export const POST_SESSION_PROCESSING_STEP_ID = 'post-session-processing-step';
export const CONFIRM_POST_SESSION_STEP_ID = 'confirm-post-session-step';

export const POST_SESSION_WORKFLOW_PROMPT_KEY = `prompts/${POST_SESSION_WORKFLOW_ID}.txt`;

/* Single shared prompt used for every non-discovery session type. */
export const POST_SESSION_WORKFLOW_GENERAL_PROMPT_KEY = `prompts/${POST_SESSION_WORKFLOW_ID}-general.txt`;

export const goalTaskSchema = z.object({
    description: z.string(),
    status: z.nativeEnum(GoalTaskStatus).default(GoalTaskStatus.IN_PROGRESS),
    position: z.number(),
});

export type WorkflowGoalTask = z.infer<typeof goalTaskSchema>;

export type WorkflowGoal = z.infer<typeof goalSchema>;

const memberSchema = z.object({
    userId: z.string(),
    firstName: z.string().nullish(),
    lastName: z.string().nullish(),
});

// Attaching category onto goal to avoid needing to handle goal group schema
// Using enum for category to avoid needing to handle goal category schema (categories are static values in the db)
export const goalSchema = z.object({
    membershipId: z.string(),
    category: z.nativeEnum(GoalCategory),
    description: z.string(),
    status: z.nativeEnum(GoalStatus).default(GoalStatus.IN_PROGRESS),
    position: z.number(),
    tasks: z.array(goalTaskSchema).nullish(),
});
export const postSessionWorkflowInputSchema = z.object({
    membershipId: z.string(),
    members: z.array(memberSchema),
    guideEmailAddress: z.string(),
    sessionId: z.string(),
    sessionType: z.nativeEnum(GuidanceSessionType),
    sessionDate: z.string(),
    attendeeTimezone: z.string().nullish(),
    transcript: z.string().nullish(),
    goalGroups: z.array(z.unknown()).nullish(),
});

export type PostSessionWorkflowInput = z.infer<
    typeof postSessionWorkflowInputSchema
>;

export const postSessionWorkflowOutputSchema = z.object({
    membershipId: z.string(),
    sessionSummary: z.string(),
    membershipNotes: z.string(),
    goals: z.array(goalSchema).nullish(),
    followUpMessage: z.string(),
});

export type PostSessionWorkflowOutput = z.infer<
    typeof postSessionWorkflowOutputSchema
>;

export const confirmPostSessionStepResumeSchema = z.object({
    confirmSummary: z.boolean().nullish(),
    confirmMembershipNotes: z.boolean().nullish(),
    confirmMessage: z.boolean().nullish(),
    confirmGoals: z.boolean().nullish(),
});

export type ConfirmPostSessionStepResume = z.infer<
    typeof confirmPostSessionStepResumeSchema
>;

export const confirmPostSessionStepOutputSchema =
    confirmPostSessionStepResumeSchema;

export type ConfirmPostSessionStepOutput = z.infer<
    typeof confirmPostSessionStepOutputSchema
>;
